import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { LeadsBySourceChart } from "@/components/dashboard/LeadsBySourceChart";
import { RecentLeads } from "@/components/dashboard/RecentLeads";
import { UpcomingAppointments } from "@/components/dashboard/UpcomingAppointments";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus, CalendarClock, Flame, BellRing, AlarmClock, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/dashboard").then(({ data }) => {
      if (active) { setData(data); setLoading(false); }
    }).catch(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";

  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const k = data.kpis;

  return (
    <div className="animate-fade-up space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
          Welcome back, {firstName}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Here's what's happening at {user?.organization?.name} today.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="kpi-todays-leads" label="Today's Leads" value={k.todays_leads} icon={UserPlus} accent hint="New enquiries captured today" />
        <KpiCard testId="kpi-todays-appointments" label="Today's Appointments" value={k.todays_appointments} icon={CalendarClock} hint="Scheduled for today" />
        <KpiCard testId="kpi-hot-leads" label="Hot Leads" value={k.hot_leads} icon={Flame} hint="High-intent prospects" />
        <KpiCard testId="kpi-pending-followups" label="Pending Follow-ups" value={k.pending_followups} icon={BellRing} hint="Awaiting action" />
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="kpi-overdue-followups" label="Overdue Follow-ups" value={k.overdue_followups} icon={AlarmClock} hint="Past due date" />
        <KpiCard testId="kpi-conversion-rate" label="Conversion Rate" value={`${k.conversion_rate}%`} icon={TrendingUp} hint="Leads → converted patients" />
        <KpiCard testId="kpi-temp-hot" label="Hot / Warm / Cold" value={`${data.lead_temperature.hot} / ${data.lead_temperature.warm} / ${data.lead_temperature.cold}`} icon={Flame} hint="Lead temperature" />
      </div>

      {/* Chart + Upcoming */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RecentLeads leads={data.recent_leads} />
        </div>
        <LeadsBySourceChart data={data.leads_by_source} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <UpcomingAppointments appointments={data.upcoming_appointments} />
        </div>
      </div>
    </div>
  );
}
